/**
 * app/navigation/NavigationContext.tsx
 * ------------------------------------
 * What: React context to hold high-level navigation state used across the app
 * (last tab, active tab, desktop layout flag) and helpers to reset navigation.
 * Why: Allows UI elements outside navigator trees to influence and reflect the
 * current section, and persists tab choice across sessions (web).
 */
import React, { createContext, useContext, useState, useEffect } from 'react';
import type { RootStackParamList } from '../types/navigation';
import { useAuth } from '../../lib/AuthContext';

// Possible tab keys: HomeTab, RoomsTab, ItemsTab, SettingsTab
const DEFAULT_TAB = 'HomeTab';

type NavigationContextType = {
  lastTab: string;
  setLastTab: (tab: string) => void;
  isDesktop: boolean;
  setIsDesktop: (isDesktop: boolean) => void;
  activeTab: keyof RootStackParamList;
  setActiveTab: (tab: keyof RootStackParamList) => void;
  resetNavigationAndStorage: () => void;
};

const NavigationContext = createContext<NavigationContextType & { resetNavigation: () => void }>(
  {
    lastTab: DEFAULT_TAB,
    setLastTab: () => {},
    isDesktop: false,
    setIsDesktop: () => {},
    activeTab: 'HomeTab',
    setActiveTab: () => {},
    resetNavigationAndStorage: () => {},
    resetNavigation: () => {},
  }
);

export function NavigationProvider({ children }: { children: React.ReactNode }) {
  const [lastTab, setLastTab] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('lastTab') || DEFAULT_TAB;
    }
    return DEFAULT_TAB;
  });
  const [isDesktop, setIsDesktop] = useState(false);
  const [activeTab, setActiveTab] = useState<keyof RootStackParamList>(() => {
    if (typeof window !== 'undefined') {
      return (localStorage.getItem('activeTab') as keyof RootStackParamList) || 'HomeTab';
    }
    return 'HomeTab';
  });
  const { user } = useAuth();
  const prevUserIdRef = React.useRef<string | null>(null);

  console.log('🧭 [NavigationContext] Provider rendered with state:', {
    lastTab,
    activeTab,
    isDesktop,
    hasUser: !!user
  });

  // Helper to reset navigation state and localStorage
  const resetNavigationAndStorage = () => {
    console.log('🔄 [NavigationContext] resetNavigationAndStorage called');
    if (typeof window !== 'undefined') {
      localStorage.removeItem('activeTab');
      localStorage.removeItem('lastTab');
    }
    setActiveTab('HomeTab');
    setLastTab('HomeTab');
  };

  // Persist tab state
  useEffect(() => {
    console.log('💾 [NavigationContext] Persisting lastTab to localStorage:', lastTab);
    if (typeof window !== 'undefined') {
      localStorage.setItem('lastTab', lastTab);
    }
  }, [lastTab]);
  
  useEffect(() => {
    console.log('💾 [NavigationContext] Persisting activeTab to localStorage:', activeTab);
    if (typeof window !== 'undefined') {
      localStorage.setItem('activeTab', activeTab);
    }
  }, [activeTab]);

  // Only reset the tab highlight the very first time a user logs in during a session.
  React.useEffect(() => {
    const currentUserId = user?.id ?? null;
    if (!prevUserIdRef.current && currentUserId) {
      console.log('👤 [NavigationContext] First login in this session, resetting to HomeTab');
      setActiveTab('HomeTab');
      setLastTab('HomeTab');
      if (typeof window !== 'undefined') {
        localStorage.setItem('activeTab', 'HomeTab');
        localStorage.setItem('lastTab', 'HomeTab');
      }
    }

    // Update previous value for next render
    prevUserIdRef.current = currentUserId;
  }, [user]);

  const resetNavigation = () => {
    console.log('🔄 [NavigationContext] resetNavigation called');
    setActiveTab('HomeTab');
    setLastTab('HomeTab');
  };

  return (
    <NavigationContext.Provider
      value={{
        lastTab,
        setLastTab,
        isDesktop,
        setIsDesktop,
        activeTab,
        setActiveTab,
        resetNavigation,
        resetNavigationAndStorage,
      }}
    >
      {children}
    </NavigationContext.Provider>
  );
}

export function useNavigationContext() {
  const context = useContext(NavigationContext);
  console.log('🧭 [useNavigationContext] Hook called, returning context');
  return context;
} 